import React, { useState } from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Typography, Tooltip, IconButton } from "@mui/material";
import { Share2, Play, Eye, Copy } from "lucide-react";
import StatusChip from "../common/StatusChip";
import PaginationFooter from "../common/PaginationFooter";
import { formatDisplayDate } from "../../utils/helperFunctions";
import { isOrphanId } from "../../utils/constants/ads";
import { getPlayableAssetUrl } from "../../utils/assetUrl";
import { moderationTableChipStatus } from "../../utils/deliveryModeration";
import { copyTextToClipboard } from "../../utils/clipboard";
import { cn } from "../../lib/utils";

const copyAssetUrl = (url) => copyTextToClipboard(url, "Asset URL copied to clipboard");

/** Map AdLaunch.status (queued/processing/success/failed) to StatusChip key, or null when no launch exists. */
const launchChipKey = (s) => {
  const v = (s || "").toLowerCase();
  if (!v) return null;
  if (v === "queued") return "launch_queued";
  if (v === "processing") return "launch_processing";
  if (v === "success") return "launch_success";
  if (v === "failed") return "launch_failed";
  return null;
};

/**
 * Moderations list table with pagination. Row click / View opens the context modal
 * (via onOpenContext), the play action opens the floating preview (via onPreview).
 */
export default function ModerationTable({
  moderations,
  isLoading,
  totalCount,
  page,
  setPage,
  pageSize,
  onPageSizeChange,
  canOpenModerationDetail,
  onOpenContext,
  onPreview,
}) {
  const [expandedAdNameId, setExpandedAdNameId] = useState(null);
  const [expandedAdsetNameId, setExpandedAdsetNameId] = useState(null);

  return (
    <Box sx={{ flex: "1 1 auto", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <TableContainer sx={{ flex: "1 1 auto", minHeight: 0 }}>
        <Table size="small" sx={{ fontFamily: "'DM Sans', system-ui, sans-serif", "& .MuiTableCell-root": { fontFamily: "'DM Sans', system-ui, sans-serif" }, "& .MuiTableBody-root .MuiTableCell-root": { borderBottom: "none" }, "& .MuiTableHead-root .MuiTableCell-root": { borderLeft: "none", borderRight: "none", borderTop: "none", backgroundColor: "#fff" } }}>
          <TableHead>
            <TableRow sx={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#fff" }}>
              <TableCell sx={{ py: 1.5, px: 2, fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Parent Ad ID</TableCell>
              <TableCell sx={{ py: 1.5, px: 2, fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Moderation ID</TableCell>
              <TableCell sx={{ py: 1.5, px: 2, fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Ad name</TableCell>
              <TableCell sx={{ py: 1.5, px: 2, fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Adset name</TableCell>
              <TableCell sx={{ py: 1.5, px: 2, fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Status</TableCell>
              <TableCell sx={{ py: 1.5, px: 2, fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Launch</TableCell>
              <TableCell sx={{ py: 1.5, px: 2, fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Iteration</TableCell>
              <TableCell sx={{ py: 1.5, px: 2, fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Delivery date</TableCell>
              <TableCell sx={{ py: 1.5, px: 2, fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                    <CircularProgress size={24} sx={{ color: "#7f7ad5" }} />
                    <Typography component="span" fontWeight={500} sx={{ color: "#7f7ad5" }}>Loading…</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : moderations.length > 0 ? (
              moderations.map((m) => {
                const adNameText = m.hook_name || m.ad_name || `Ad ${m.hook_number ?? 1}`;
                const adsetText = m.adset_name || "";
                const launchKey = launchChipKey(m.launch_status);
                return (
                <TableRow
                  key={m.id}
                  onClick={() => {
                    if (!canOpenModerationDetail) return;
                    const sel = (typeof window !== "undefined" && window.getSelection && window.getSelection().toString()) || "";
                    if (sel.length > 0) return; // allow text selection without triggering navigation
                    if (m.ad_id != null && m.ad_id !== -7) {
                      onOpenContext(m.ad_id, m.id ?? null);
                    }
                  }}
                  sx={{
                    cursor: canOpenModerationDetail && m.ad_id != null && m.ad_id !== -7 ? "pointer" : "default",
                    "&:hover": { backgroundColor: "inherit !important" },
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      {m.ad_id != null && m.ad_id !== -7 ? m.ad_id : "—"}
                      {isOrphanId(m.ad_request_id) && (
                        <span className="text-amber-600 font-medium" title="No linked ad request">O</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{m.id ?? "—"}</TableCell>
                  <TableCell sx={{ color: "text.secondary", maxWidth: 220 }}>
                    <div className="flex items-center gap-1 max-w-[200px]">
                      <Tooltip title="Copy">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); copyTextToClipboard(adNameText); }}
                          aria-label="Copy ad name"
                          sx={{ p: 0.25 }}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </IconButton>
                      </Tooltip>
                      <span
                        className={cn("select-text text-sm cursor-pointer hover:underline", expandedAdNameId === m.id ? "break-all" : "truncate block max-w-[160px]")}
                        onClick={(e) => {
                          e.stopPropagation();
                          const sel = (typeof window !== "undefined" && window.getSelection && window.getSelection().toString()) || "";
                          if (sel.length > 0) return;
                          setExpandedAdNameId((prev) => prev === m.id ? null : m.id);
                        }}
                        title="Click to expand"
                      >
                        {adNameText}
                      </span>
                      {m.asset_url ? (
                        m.s3_asset_url ? (
                          <span title="Migrated to S3 (CDN playable)" style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: "#10b981", flexShrink: 0 }} />
                        ) : (
                          <span title="Pending S3 migration (Drive only)" style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: "#f59e0b", flexShrink: 0 }} />
                        )
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", maxWidth: 180 }}>
                    <div className="flex items-center gap-1 max-w-[160px]">
                      {adsetText && (
                        <Tooltip title="Copy">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); copyTextToClipboard(adsetText); }}
                            aria-label="Copy adset name"
                            sx={{ p: 0.25 }}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <span
                        className={cn("select-text text-sm cursor-pointer hover:underline", expandedAdsetNameId === m.id ? "break-all" : "truncate block max-w-[120px]")}
                        onClick={(e) => {
                          e.stopPropagation();
                          const sel = (typeof window !== "undefined" && window.getSelection && window.getSelection().toString()) || "";
                          if (sel.length > 0) return;
                          setExpandedAdsetNameId((prev) => prev === m.id ? null : m.id);
                        }}
                        title="Click to expand"
                      >
                        {adsetText || "—"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusChip subtle status={moderationTableChipStatus(m)} />
                  </TableCell>
                  <TableCell>
                    {launchKey ? <StatusChip subtle status={launchKey} /> : <span className="text-gray-400 text-xs">—</span>}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{m.iteration ?? 0}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{formatDisplayDate(m.created_on) ?? "—"}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box display="flex" gap={0.5} alignItems="center">
                      {canOpenModerationDetail && (
                        <Tooltip title="Preview">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onPreview(getPlayableAssetUrl({ s3_asset_url: m.s3_asset_url, ad_asset_url: m.asset_url }));
                              }}
                              disabled={!canOpenModerationDetail}
                              aria-label="Preview"
                            >
                              <Play className="w-4 h-4" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {canOpenModerationDetail && (
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (m.ad_id != null && m.ad_id !== -7) {
                                onOpenContext(m.ad_id, m.id ?? null);
                              }
                            }}
                            aria-label="View"
                          >
                            <Eye className="w-4 h-4" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canOpenModerationDetail && (
                        <Tooltip title="Share">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyAssetUrl(m.asset_url);
                            }}
                            aria-label="Share"
                          >
                            <Share2 className="w-4 h-4" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  No moderations found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationFooter
        shown={moderations.length}
        totalCount={totalCount}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />
    </Box>
  );
}
